import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Headline, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ApiLearnerManualGoalSyncStatusChoices } from "@api/graphql/types-and-hooks";
import { FilteredGoalsAndTargets } from "@components/TreatmentPlan/ProgrammingGoalCard/FilteredGoalsAndTargets";
import { GoalHeader } from "@components/TreatmentPlan/ProgrammingGoalCard/GoalHeader";
import { ProgrammingGoalCard } from "@components/TreatmentPlan/ProgrammingGoalCard/ProgrammingGoalCard";
import { AddProgrammingDialogButton } from "@pages/ClientProgramming/AddProgrammingDialogButton";
import ProgrammingGoal from "@pages/ClientProgramming/ProgrammingGoal/ProgrammingGoal";
import { useBreakpoint } from "@pages/VideoSessions/hooks/useMediaMatcher";
import { useAdminData } from "@providers/AdminDataProvider";
import { GoalList, Target, useClientProgrammingData } from "@providers/ClientProgrammingProvider";

interface ClientProgrammingProps {}

export const ClientProgramming: React.FC<ClientProgrammingProps> = ({}) => {
  const { isMobile } = useBreakpoint();
  const { goalId } = useParams();

  const {
    getClientGoalDataQuery: { isFetching, data: clientGoalData },
    goalList,
    selectedGoal,
    selectedTarget,
    searchGoalText,
    selectedGoalStatus,
    goalRefreshStatus,
    goalRefreshLoading,
    latestInitialAssessmentReportQuery,
  } = useClientProgrammingData();

  const { isPassageHealthUser } = useAdminData();

  const [filteredGoals, setFilteredGoals] = useState<GoalList>([]);
  const [filteredTargets, setFilteredTargets] = useState<Target[]>([]);
  const [collapseAll, setCollapseAll] = useState<boolean>(true);

  const showGoalDetails = goalId && (selectedGoal || selectedTarget);

  useEffect(() => {
    if (!goalList?.length) return;

    const trimmedSearch = searchGoalText.trim().toLowerCase();

    const goals: GoalList = [];
    const targets: Target[] = [];

    goalList.forEach((goal) => {
      const goalMatches = trimmedSearch ? goal.name?.toLowerCase().includes(trimmedSearch) : true;

      const matchingTargets = trimmedSearch
        ? goal.targets?.filter((t) => t.name?.toLowerCase().includes(trimmedSearch)) || []
        : goal.targets || [];

      if (goalMatches) {
        goals.push(goal); // add entire goal
      }

      if (matchingTargets.length) {
        targets.push(...matchingTargets); // add matching targets only
      }
    });

    setFilteredGoals(goals);
    setFilteredTargets(targets);
  }, [searchGoalText, goalList]);

  if (isFetching) {
    return (
      <div className="w-full min-h-[calc(100vh-14rem)] flex justify-center items-center">
        <Paragraph size="large" colorType="primary" displayType="loud">
          Loading...
        </Paragraph>
      </div>
    );
  }

  const isGoalRefreshInProgress =
    goalRefreshLoading || goalRefreshStatus === ApiLearnerManualGoalSyncStatusChoices.Inprogress;

  // Passage clients only get the view-only notice once they have programming.
  const isViewOnly = !isPassageHealthUser || !!clientGoalData?.goals?.edges?.length;

  return (
    <div className="flex flex-col w-full h-full overflow-x-hidden">
      {isViewOnly && (
        <div className="d-flex sticky w-full z-20 px-4 py-2 bg-blue-50 flex justify-center">
          <Label size="small" colorType="primary" className="text-center">
            This client's programming is view-only.
          </Label>
        </div>
      )}
      <div className="w-full h-full flex justify-center items-center">
        {goalList?.length || selectedGoalStatus ? (
          <div className="flex h-full w-full">
            {/* left column */}
            <div className="w-1/4 shrink-0 pr-4">
              <div>
                <GoalHeader
                  goalList={goalList || []}
                  collapseAll={collapseAll}
                  setCollapseAll={setCollapseAll}
                  reportId={latestInitialAssessmentReportQuery.data?.getReports?.edges[0]?.node?.id || null}
                />
              </div>
              <div
                className={clsx(
                  "overflow-y-auto max-h-[calc(100vh-17.5rem)] min-h-[calc(100vh-17.5rem)]",
                  isGoalRefreshInProgress && "pointer-events-none opacity-30 cursor-not-allowed",
                )}
              >
                {searchGoalText ? (
                  <FilteredGoalsAndTargets filteredGoals={filteredGoals} filteredTargets={filteredTargets} />
                ) : (
                  goalList?.map((goal) => <ProgrammingGoalCard key={goal.id} goal={goal} collapseAll={collapseAll} />)
                )}
              </div>
            </div>
            {/* right column */}
            <div className="flex-1 flex flex-col px-4 border-l border-limestone-100 max-h-[calc(100vh-14rem)] overflow-y-auto">
              {showGoalDetails ? (
                <ProgrammingGoal />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center gap-3">
                  <Headline size="extra-small">No Program Selected</Headline>
                  <Paragraph colorType="secondary">
                    Click on a goal or target from the list to see more information.
                  </Paragraph>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`flex flex-col justify-center min-h-[calc(100vh-17.5rem)] items-center gap-8 ${isMobile ? "w-full" : "w-3/4"} px-8`}
          >
            <div className="flex flex-col items-center gap-3 pt-4">
              <Headline size="extra-small">No programs yet</Headline>
              <Paragraph colorType="secondary">
                Upload a file, drop in a quick screenshot, or manually enter your goals and programs. The more context
                you provide, the smarter and more personalized your session insights will be.
              </Paragraph>
            </div>
            <AddProgrammingDialogButton />
          </div>
        )}
      </div>
    </div>
  );
};
